/*
  Teachers List filter controller.

  Scoped to the Teachers List section only. Handles the LOCATION filtering of the
  teacher cards (by their normalized `data-tags`).

  With the default order, pagination is the standard load-more-pagination snippet;
  when it appends more cards we re-scan so newly loaded cards respect the active
  filter. With random and descending, every card is in the DOM: random is shuffled
  with a seed kept for the browser session, descending is reversed, and both reveal
  cards in batches by the script.
*/
if (!customElements.get('teachers-filter')) {
  // Block-scoped (not top-level): this file and private-classes-filter.js are both
  // loaded as classic scripts, so top-level const/let would share one global scope
  // across files. Both once declared identically-named seed/shuffle helpers at top
  // level, and whichever loaded second failed to parse at all — silently killing its
  // customElements.define along with everything else in the file.
  const TEACHERS_ORDER_SEED_KEY = 'teachers-order-seed';
  let fallbackOrderSeed = null;

  const createRandom = (seed) => () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const getSessionOrderSeed = () => {
    try {
      let seed = sessionStorage.getItem(TEACHERS_ORDER_SEED_KEY);
      if (!seed) {
        seed = String(Math.floor(Math.random() * 4294967296));
        sessionStorage.setItem(TEACHERS_ORDER_SEED_KEY, seed);
      }
      return Number(seed) >>> 0;
    } catch (e) {
      if (fallbackOrderSeed === null) {
        fallbackOrderSeed = Math.floor(Math.random() * 4294967296);
      }
      return fallbackOrderSeed;
    }
  };

  customElements.define(
    'teachers-filter',
    class TeachersFilter extends HTMLElement {
      // connectedCallback must stay a prototype method — the Custom Elements API
      // reads lifecycle callbacks off the prototype when the element is defined.
      connectedCallback() {
        this.grid = this.querySelector('[data-teachers-grid]');
        this.countEl = this.querySelector('[data-teachers-count]');

        if (!this.grid) return;

        document.addEventListener('load-more:loaded', this.handleLoadMoreLoaded);

        if (this.initialized) {
          this.render();
          return;
        }
        this.initialized = true;

        this.heading = this.dataset.heading || '';
        // Preserve the server-rendered count (total) to restore when no filter is active.
        this.initialCountText = this.countEl ? this.countEl.textContent.trim() : '';
        this.activeFilters = new Set();

        this.isBatched = this.dataset.order === 'random' || this.dataset.order === 'descending';
        this.perPage = Number(this.dataset.perPage) || 12;
        this.visibleLimit = this.perPage;

        if (this.dataset.order === 'random') this.shuffleCards();
        if (this.dataset.order === 'descending') this.reverseCards();
        if (this.isBatched) this.setAttribute('data-ordered', '');

        this.cards = Array.from(this.grid.querySelectorAll('[data-tags]'));
        this.cards.forEach((card) => card.removeAttribute('data-overflow'));
        this.checkboxes = Array.from(this.querySelectorAll('[data-teacher-filter]'));
        this.applyBtn = this.querySelector('[data-teacher-filter-apply]');
        this.resetBtn = this.querySelector('[data-teacher-filter-reset]');
        this.loadMoreWrap = this.querySelector('[data-teachers-load-more]');
        this.loadMoreBtn = this.querySelector('[data-teachers-load-more-button]');
        this.statusEl = this.querySelector('[data-teachers-status]');
        this.statusTemplate = this.statusEl ? this.statusEl.dataset.statusTemplate || '' : '';
        this.nextCountEl = this.querySelector('[data-teachers-next-count]');
        this.totalWrapEl = this.querySelector('.load-more-pagination__total');
        this.totalCountEl = this.querySelector('[data-teachers-total-count]');
        this.endEl = this.querySelector('[data-teachers-end-message]');
        this.endCountEl = this.querySelector('[data-teachers-end-count]');

        // Match the collection filter: apply immediately when a value changes.
        this.checkboxes.forEach((cb) => {
          cb.addEventListener('change', () => this.applyFilters());
        });
        // The Apply button only closes the drawer (filtering already happened).
        if (this.applyBtn) {
          this.applyBtn.addEventListener('click', () => this.closeDrawer());
        }
        if (this.resetBtn) {
          this.resetBtn.addEventListener('click', (e) => {
            e.preventDefault();
            if (this.resetBtn.classList.contains('disabled')) return;
            this.reset();
          });
        }
        if (this.loadMoreBtn) {
          this.loadMoreBtn.addEventListener('click', this.showMore);
        }

        this.setupCollapsibleDetails();
        this.render();
        this.announceEnabled = true;
      }

      disconnectedCallback() {
        document.removeEventListener('load-more:loaded', this.handleLoadMoreLoaded);
      }

      handleLoadMoreLoaded = () => {
        this.cards = Array.from(this.grid.querySelectorAll('[data-tags]'));
        this.render();
      };

      reverseCards = () => {
        const cards = Array.from(this.grid.querySelectorAll('[data-tags]'));
        cards.reverse().forEach((card) => this.grid.appendChild(card));
      };

      shuffleCards = () => {
        const random = createRandom(getSessionOrderSeed());
        const cards = Array.from(this.grid.querySelectorAll('[data-tags]'));
        for (let i = cards.length - 1; i > 0; i--) {
          const j = Math.floor(random() * (i + 1));
          [cards[i], cards[j]] = [cards[j], cards[i]];
        }
        cards.forEach((card) => this.grid.appendChild(card));
      };

      showMore = () => {
        const firstNew = this.getMatched()[this.visibleLimit];
        this.visibleLimit += this.perPage;
        this.render();
        const target = firstNew && firstNew.querySelector('a, button');
        if (target) target.focus();
      };

      setupCollapsibleDetails = () => {
        const ANIMATING_ATTR = 'data-animating';

        this.querySelectorAll('.mobile-facets__details').forEach((details) => {
          const summary = details.querySelector('.mobile-facets__summary');
          const submenu = details.querySelector('.mobile-facets__submenu');
          if (!summary || !submenu) return;

          summary.addEventListener('click', (e) => {
            e.preventDefault();
            if (submenu.hasAttribute(ANIMATING_ATTR)) return;
            if (details.hasAttribute('open')) {
              this.animateClose(details, summary, submenu, ANIMATING_ATTR);
            } else {
              this.animateOpen(details, summary, submenu, ANIMATING_ATTR);
            }
          });
        });
      };

      animateOpen = (details, summary, submenu, animatingAttr) => {
        details.setAttribute('open', '');
        details.classList.add('is-expanded');
        summary.setAttribute('aria-expanded', 'true');
        submenu.setAttribute(animatingAttr, 'open');
        submenu.style.height = '0px';
        requestAnimationFrame(() => {
          submenu.style.height = submenu.scrollHeight + 'px';
        });
        const onEnd = (e) => {
          if (e.propertyName !== 'height' || e.target !== submenu) return;
          submenu.removeEventListener('transitionend', onEnd);
          submenu.style.height = '';
          submenu.removeAttribute(animatingAttr);
        };
        submenu.addEventListener('transitionend', onEnd);
      };

      animateClose = (details, summary, submenu, animatingAttr) => {
        submenu.setAttribute(animatingAttr, 'close');
        details.classList.remove('is-expanded');
        summary.setAttribute('aria-expanded', 'false');
        submenu.style.height = submenu.scrollHeight + 'px';
        submenu.offsetHeight; // force reflow
        submenu.style.height = '0px';
        const onEnd = (e) => {
          if (e.propertyName !== 'height' || e.target !== submenu) return;
          submenu.removeEventListener('transitionend', onEnd);
          details.removeAttribute('open');
          submenu.style.height = '';
          submenu.removeAttribute(animatingAttr);
        };
        submenu.addEventListener('transitionend', onEnd);
      };

      getMatched = () => {
        if (this.activeFilters.size === 0) return this.cards;
        return this.cards.filter((card) => {
          const tags = (card.dataset.tags || '').split(',').filter(Boolean);
          return tags.some((t) => this.activeFilters.has(t));
        });
      };

      applyFilters = () => {
        this.activeFilters = new Set(this.checkboxes.filter((cb) => cb.checked).map((cb) => cb.value));
        this.visibleLimit = this.perPage;
        this.render();
      };

      closeDrawer = () => {
        const drawer = this.querySelector('facet-drawer');
        if (drawer && typeof drawer.close === 'function') drawer.close();
      };

      reset = () => {
        this.checkboxes.forEach((cb) => {
          cb.checked = false;
        });
        this.activeFilters = new Set();
        this.visibleLimit = this.perPage;
        this.render();
      };

      formatCount = (n) => (n < 10 ? '0' + n : String(n));

      render = () => {
        const matched = this.getMatched();
        const visible = this.isBatched ? new Set(matched.slice(0, this.visibleLimit)) : new Set(matched);

        this.cards.forEach((card) => {
          card.hidden = !visible.has(card);
        });

        if (this.loadMoreWrap) {
          const remaining = matched.length - this.visibleLimit;
          const hasMore = remaining > 0;
          this.loadMoreBtn.hidden = !hasMore;
          this.totalWrapEl.hidden = !hasMore;
          this.endEl.hidden = hasMore;
          this.nextCountEl.textContent = Math.max(0, Math.min(this.perPage, remaining));
          this.totalCountEl.textContent = matched.length;
          this.endCountEl.textContent = matched.length;
        }

        if (this.countEl) {
          if (this.activeFilters.size === 0) {
            // No filter: keep the server-rendered total count.
            this.countEl.textContent = this.initialCountText;
          } else {
            const prefix = this.heading ? this.heading + ' – ' : '';
            this.countEl.textContent = prefix + this.formatCount(matched.length);
          }
        }

        if (this.statusEl && this.announceEnabled) {
          const shown = this.isBatched ? Math.min(this.visibleLimit, matched.length) : matched.length;
          this.statusEl.textContent = this.statusTemplate
            .replace('SHOWN_PLACEHOLDER', shown)
            .replace('TOTAL_PLACEHOLDER', matched.length);
        }

        this.updateResetState();
      };

      // Disable RESET ALL while no filter is selected (matches the collection filter).
      updateResetState = () => {
        if (!this.resetBtn) return;
        const hasActiveFilters = this.activeFilters.size > 0;
        this.resetBtn.classList.toggle('disabled', !hasActiveFilters);
        this.resetBtn.setAttribute('aria-disabled', String(!hasActiveFilters));
      };
    },
  );
}
